/* The x402 storefront's page module: code tabs (switch + copy), the /scan
   "Try it from your wallet" panel and the /storage "Read your backups"
   panel. No wallet code loads on page view: the first click on a wallet
   button dynamic-imports the hashed wallet bundle named in the section's
   data-wallet-src (taken from the wallet build's manifest on the server).
   Results come back as server-rendered partials that htmx swaps in. */
(function () {
  'use strict'

  function each(root, sel, fn) {
    Array.prototype.forEach.call(root.querySelectorAll(sel), fn)
  }

  function shortAddr(a) {
    return a.length > 12 ? a.slice(0, 6) + '…' + a.slice(-4) : a
  }

  /* -- code tabs ------------------------------------------------------------ */
  /* Copy a panel's text. Without the async clipboard API (an insecure context, an old browser) or when it refuses, the text is selected so the reader can copy it by hand. */
  function copyText(node, done) {
    function select() {
      var range = document.createRange()
      range.selectNodeContents(node)
      var sel = window.getSelection()
      sel.removeAllRanges()
      sel.addRange(range)
    }
    try {
      navigator.clipboard.writeText(node.textContent).then(done, function (err) {
        console.warn('[x402] clipboard write failed:', err)
        select()
      })
    } catch (err) {
      console.warn('[x402] clipboard API unavailable:', err)
      select()
    }
  }

  function initTabs(root) {
    var copyBtn = root.querySelector('[data-copy-code]')
    var copyLabel = copyBtn ? copyBtn.textContent : ''
    var timer = 0
    each(root, '[data-tab]', function (tab) {
      tab.addEventListener('click', function () {
        var key = tab.getAttribute('data-tab')
        each(root, '[data-tab]', function (t) {
          t.setAttribute('aria-selected', t === tab ? 'true' : 'false')
        })
        each(root, '[data-tab-panel]', function (p) { p.hidden = p.getAttribute('data-tab-panel') !== key })
      })
    })
    if (copyBtn) {
      copyBtn.addEventListener('click', function () {
        var panel = root.querySelector('[data-tab-panel]:not([hidden])')
        if (!panel) return
        copyText(panel, function () {
          copyBtn.textContent = copyBtn.getAttribute('data-label-copied')
          clearTimeout(timer)
          timer = setTimeout(function () { copyBtn.textContent = copyLabel }, 1600)
        })
      })
    }
  }

  /* -- wallet bundle (loaded on the first click only) ------------------------ */
  var walletLoad = null
  function loadWallet(root) {
    if (!walletLoad) {
      walletLoad = import(root.getAttribute('data-wallet-src')).then(function (w) {
        w.configure({
          algodApiUrl: root.getAttribute('data-wallet-algod'),
          walletConnectChainId: Number(root.getAttribute('data-wallet-chain'))
        })
        return w
      })
      walletLoad.catch(function (err) {
        console.warn('[x402] wallet bundle failed to load:', err)
        walletLoad = null
      })
    }
    return walletLoad
  }

  function errorText(root, e) {
    return e && e.message ? e.message : root.getAttribute('data-label-generic')
  }

  /* Shared connect/disconnect state of one wallet panel. */
  function walletPanel(root, button, idleLabel, readyLabel) {
    var connected = root.querySelector('[data-connected]')
    var connectedText = root.querySelector('[data-connected-text]')
    var errorEl = root.querySelector('[data-wallet-error]')
    var state = { address: null, busy: false }

    function showError(msg) {
      errorEl.textContent = msg || ''
      errorEl.hidden = !msg
    }
    function render() {
      if (!state.busy) button.textContent = state.address ? readyLabel : idleLabel
      connected.hidden = !state.address
      if (state.address) {
        connectedText.textContent = root
          .getAttribute('data-label-connected-as')
          .replace('{address}', shortAddr(state.address))
      }
    }
    function setBusy(label) {
      state.busy = !!label
      button.disabled = !!label
      if (label) button.textContent = label
      else render()
    }
    function connect() {
      showError(null)
      setBusy(root.getAttribute('data-label-connecting'))
      return loadWallet(root)
        .then(function (w) { return w.connect() })
        .then(function (addr) {
          state.address = addr
          return addr
        })
        .catch(function (e) {
          showError(errorText(root, e))
          return null
        })
        .then(function (addr) {
          setBusy(null)
          return addr
        })
    }
    root.querySelector('[data-disconnect]').addEventListener('click', function () {
      if (state.busy) return
      loadWallet(root)
        .then(function (w) { return w.disconnect() })
        .catch(function (e) { console.warn('[x402] wallet disconnect failed:', e) })
        .then(function () {
          state.address = null
          showError(null)
          render()
        })
    })
    return { state: state, connect: connect, showError: showError, setBusy: setBusy, render: render }
  }

  function swap(root, url, verb, values) {
    var target = root.querySelector('[data-result-target]')
    return window.htmx.ajax(verb, url, { target: target, swap: 'innerHTML', values: values })
  }

  /* -- /scan: connect, then pay one scan ------------------------------------- */
  function initScan(root) {
    var form = root.querySelector('[data-scan-form]')
    var input = root.querySelector('[data-scan-target]')
    var button = root.querySelector('[data-scan-button]')
    var panel = walletPanel(root, button, root.getAttribute('data-label-connect'), root.getAttribute('data-label-pay'))
    var attempt = 0

    form.addEventListener('submit', function (e) {
      e.preventDefault()
      if (panel.state.busy) return
      if (!panel.state.address) {
        panel.connect()
        return
      }
      var target = input.value.trim()
      if (!/^https?:\/\/\S+$/i.test(target)) {
        panel.showError(root.getAttribute('data-label-invalid-url'))
        return
      }
      var mine = ++attempt
      panel.showError(null)
      input.disabled = true
      panel.setBusy(root.getAttribute('data-step-challenging'))
      loadWallet(root)
        .then(function (w) {
          return w.pay({
            url: root.getAttribute('data-scan-url'),
            body: { url: target },
            payerAddress: panel.state.address,
            onStep: function (step) {
              if (mine === attempt) panel.setBusy(root.getAttribute('data-step-' + step) || '…')
            }
          })
        })
        .then(
          function (res) {
            return { kind: 'success', settlementTxId: res.settlementTxId, report: res.body || {} }
          },
          function (err) {
            return {
              kind: 'error',
              message: errorText(root, err),
              settled: !!(err && err.settled),
              settlementTxId: (err && err.settlementTxId) || null
            }
          }
        )
        .then(function (outcome) {
          if (mine !== attempt) return
          return swap(root, root.getAttribute('data-result-url'), 'POST', { result: JSON.stringify(outcome) })
        })
        .catch(function (err) { panel.showError(errorText(root, err)) })
        .then(function () {
          if (mine !== attempt) return
          input.disabled = false
          panel.setBusy(null)
        })
    })
    panel.render()
  }

  /* -- /storage: connect, then sign a nonce and list the backups ------------- */
  function initStorage(root) {
    var form = root.querySelector('[data-storage-form]')
    var button = root.querySelector('[data-storage-button]')
    var panel = walletPanel(root, button, root.getAttribute('data-label-connect'), root.getAttribute('data-label-list'))

    form.addEventListener('submit', function (e) {
      e.preventDefault()
      if (panel.state.busy) return
      if (!panel.state.address) {
        panel.connect()
        return
      }
      var wallet = panel.state.address
      panel.showError(null)
      panel.setBusy(root.getAttribute('data-label-signing'))
      var challenge
      fetch(root.getAttribute('data-challenge-url'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ wallet: wallet })
      })
        .then(function (res) {
          return res.json().then(function (body) {
            if (!res.ok || !body || !body.nonce || !body.signing_message) {
              var msg = body && body.error && body.error.message
              throw new Error(msg || 'HTTP ' + res.status)
            }
            challenge = body
            return loadWallet(root)
          })
        })
        .then(function (w) { return w.signStorageChallenge(wallet, challenge.signing_message) })
        .then(function (signature) {
          panel.setBusy(root.getAttribute('data-label-loading'))
          var qs = new URLSearchParams({
            wallet: wallet,
            nonce: challenge.nonce,
            proof_method: 'signed_bytes',
            signature_b64: signature
          })
          return swap(root, root.getAttribute('data-backups-url') + '?' + qs.toString(), 'GET')
        })
        .catch(function (err) { panel.showError(errorText(root, err)) })
        .then(function () { panel.setBusy(null) })
    })
    panel.render()
  }

  function init() {
    each(document, '[data-code-tabs]', initTabs)
    each(document, '[data-x402-scan]', initScan)
    each(document, '[data-x402-storage]', initStorage)
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init)
  else init()
})()
